import { useState } from "react";
import {
  Badge, Button, Card, CardSection, EmptyState, Field, Input, Textarea, UploadField,
} from "@/components/ui";
import { slugify } from "@/lib/utils/format";
import type { Scene } from "@/lib/types/config";
import { useBuilder } from "../BuilderContext";
import { IconGallery, IconPlus, IconTrash } from "../components/icons";
import { PromptHelp } from "../components/PromptHelp";

/**
 * "Vibes" — the visual scenes a guest picks from. Each becomes an AI backdrop.
 *
 * A Vibe's `default_prompt_template` is what runs when there are no
 * participants, so for most single-brand events this tab carries the entire
 * creative direction of the booth.
 */
export function VibesTab() {
  const { draft, update } = useBuilder();
  const scenes = draft.scenes ?? [];
  const scenarios = draft.scenarios ?? [];
  const [expanded, setExpanded] = useState<string | null>(scenes[0]?.slug ?? null);

  function setScenes(next: Scene[]) {
    update((current) => ({ ...current, scenes: next }));
  }

  function add() {
    const stamp = Date.now();
    const scene: Scene = { slug: `scene-${stamp}`, name: "New Vibe", sort_order: scenes.length };
    setScenes([...scenes, scene]);
    setExpanded(scene.slug);
  }

  function patch(index: number, patchValue: Partial<Scene>) {
    setScenes(scenes.map((item, i) => (i === index ? { ...item, ...patchValue } : item)));
  }

  function remove(index: number) {
    const target = scenes[index];
    update((current) => ({
      ...current,
      // Keep the scene deletion and all dependent-reference cleanup in one
      // draft update. Separate updates can race and restore the old scenes
      // array, especially when deleting the final vibe.
      scenes: (current.scenes ?? []).filter((_, i) => i !== index),
      scenarios: (current.scenarios ?? []).filter((s) => s.scene_slug !== target?.slug),
      // Moments can be scoped to scenes; a dangling slug fails cross-reference
      // validation on publish.
      moments: (current.moments ?? []).map((moment) => ({
        ...moment,
        scene_slugs: moment.scene_slugs?.filter((slug) => slug !== target?.slug),
      })),
    }));
    setExpanded((current) => current === target?.slug ? null : current);
  }

  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= scenes.length) return;
    const next = [...scenes];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    setScenes(next.map((scene, i) => ({ ...scene, sort_order: i })));
  }

  return (
    <CardSection
      eyebrow="Vibe"
      title="Vibes"
      description="Visual vibes — each one becomes a distinct AI backdrop guests can choose."
      actions={<Button variant="outline" size="sm" icon={<IconPlus size={14} />} onClick={add}>Add Scene</Button>}
    >
      {scenes.length === 0 ? (
        <EmptyState
          icon={<IconGallery size={28} />}
          title="No vibes yet"
          description="A booth needs at least one vibe — it's what the AI actually renders."
          action={<Button variant="primary" size="sm" onClick={add}>Add the first vibe</Button>}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {scenes.map((scene, index) => {
            const open = expanded === scene.slug;
            const usage = scenarios.filter((s) => s.scene_slug === scene.slug).length;

            return (
              <Card key={`${scene.slug}-${index}`} className="overflow-hidden">
                <header className="flex items-center gap-3 p-4">
                  <button
                    onClick={() => setExpanded(open ? null : scene.slug)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    <span className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-lg border border-ink-600 bg-ink-800">
                      {scene.image_path ? (
                        <img src={scene.image_path} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <IconGallery size={15} className="text-haze-400" />
                      )}
                    </span>
                    <span className="flex min-w-0 flex-col leading-tight">
                      <span className="truncate text-sm font-semibold">{scene.name || "Untitled"}</span>
                      <span className="truncate font-mono text-2xs text-haze-400">{scene.slug}</span>
                    </span>
                  </button>

                  <div className="flex items-center gap-1">
                    {usage > 0 && <Badge tone="aqua">{usage}</Badge>}
                    {!scene.default_prompt_template && usage === 0 && <Badge tone="warn">No prompt</Badge>}
                    <Button variant="ghost" size="sm" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Move up">↑</Button>
                    <Button variant="ghost" size="sm" onClick={() => move(index, 1)} disabled={index === scenes.length - 1} aria-label="Move down">↓</Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(index)} aria-label={`Remove ${scene.name}`} className="text-haze-400 hover:text-danger">
                      <IconTrash size={14} />
                    </Button>
                  </div>
                </header>

                {open && (
                  <div className="flex flex-col gap-5 border-t border-ink-600 p-5">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Slug">
                        <Input
                          className="font-mono"
                          value={scene.slug}
                          onChange={(e) => {
                            const nextSlug = slugify(e.target.value);
                            const previous = scene.slug;
                            patch(index, { slug: nextSlug });
                            setExpanded(nextSlug);
                            update((current) => ({
                              ...current,
                              scenarios: (current.scenarios ?? []).map((s) =>
                                s.scene_slug === previous ? { ...s, scene_slug: nextSlug } : s,
                              ),
                              moments: (current.moments ?? []).map((moment) => ({
                                ...moment,
                                scene_slugs: moment.scene_slugs?.map((slug) => (slug === previous ? nextSlug : slug)),
                              })),
                            }));
                          }}
                        />
                      </Field>

                      <Field label="Name">
                        <Input value={scene.name} onChange={(e) => patch(index, { name: e.target.value })} />
                      </Field>
                    </div>

                    <Field label="Description" hint="Shown under the vibe's name on the selection screen.">
                      <Textarea rows={2} value={scene.description ?? ""} onChange={(e) => patch(index, { description: e.target.value })} />
                    </Field>

                    <Field
                      label="Default prompt template"
                      hint="Used when no participant is selected. With participants enabled, the scenario prompt wins."
                    >
                      <Textarea
                        rows={6}
                        className="font-mono text-xs"
                        value={scene.default_prompt_template ?? ""}
                        onChange={(e) => patch(index, { default_prompt_template: e.target.value })}
                      />
                    </Field>

                    <PromptHelp />

                    <UploadField
                      label="Style reference image"
                      value={scene.style_reference_image}
                      onChange={(value) => patch(index, { style_reference_image: value })}
                      hint="Passed to the model alongside the prompt to lock a look. Strong influence — use a clean reference."
                    />

                    <UploadField
                      label="Theme reference image"
                      value={scene.image_path}
                      onChange={(value) => patch(index, { image_path: value })}
                      hint="The thumbnail guests tap on the vibe selection screen."
                    />
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </CardSection>
  );
}
